import { BellIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context';
import {
  NotificationCategory,
  getAvailableCategoriesForRoles,
} from '../../constants/notificationCategories';
import { ToggleSwitch } from '../../tailwindComponents/ToggleSwitch/ToggleSwitch';
import { Select } from '../../tailwindComponents/Select/Select';
import { useNotificationPreferences } from '../../services/useNotificationPreferences';
import { DigestFrequency } from '../../services/types';

const FREQUENCY_OPTIONS: { label: string; value: DigestFrequency }[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Off', value: 'off' },
];

export function NotificationPreferencesCard() {
  const { user } = useAuth();
  const { preferences, loading, saving, error, updatePreferences } =
    useNotificationPreferences();

  const userRoles = user?.roles ?? [];
  const availableCategories = getAvailableCategoriesForRoles(userRoles);

  if (loading && !preferences) {
    return (
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
        <p className='text-sm text-gray-500'>
          Loading notification preferences…
        </p>
      </div>
    );
  }

  const current = preferences ?? {
    userId: user?.id ?? '',
    emailDigestEnabled: false,
    digestFrequency: 'weekly' as DigestFrequency,
    categoryEnabled: {
      warehouse: true,
      operations: true,
      transport: true,
      finance: true,
      general: true,
    },
  };

  const save = (next: Partial<typeof current>) => {
    updatePreferences({
      emailDigestEnabled:
        next.emailDigestEnabled ?? current.emailDigestEnabled,
      digestFrequency: next.digestFrequency ?? current.digestFrequency,
      categoryEnabled: next.categoryEnabled ?? current.categoryEnabled,
    }).catch(() => {});
  };

  const handleCategoryToggle = (categoryId: NotificationCategory, enabled: boolean) => {
    save({
      categoryEnabled: { ...current.categoryEnabled, [categoryId]: enabled },
    });
  };

  const handleDigestToggle = (enabled: boolean) => {
    save({ emailDigestEnabled: enabled });
  };

  const handleFrequencyChange = (value: string | number) => {
    save({ digestFrequency: value as DigestFrequency });
  };

  const frequencySelected =
    FREQUENCY_OPTIONS.find((o) => o.value === current.digestFrequency) ??
    FREQUENCY_OPTIONS[1];

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
      <div className='border-b border-gray-200 bg-gray-50 px-6 py-4'>
        <div className='flex items-center gap-3'>
          <BellIcon className='h-5 w-5 text-gray-400' />
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>
              Notification Preferences
            </h2>
            <p className='mt-0.5 text-sm text-gray-500'>
              Choose which notifications you want to receive and when.
            </p>
          </div>
          {saving && (
            <span className='ml-auto text-xs text-gray-400'>Saving…</span>
          )}
        </div>
      </div>

      <div className='p-6 space-y-6'>
        <div className='space-y-3'>
          <h3 className='text-sm font-semibold text-gray-700'>Topics</h3>
          <p className='text-xs text-gray-500'>
            Turning a topic off stops it from appearing in your inbox or in the
            digest email.
          </p>
          {availableCategories.map((category) => (
            <div key={category.id}>
              <ToggleSwitch
                label={category.label}
                checked={current.categoryEnabled[category.id] !== false}
                onChange={(checked) => handleCategoryToggle(category.id, checked)}
                yesLabel='On'
                noLabel='Off'
              />
              <p className='text-xs text-gray-500 mt-1 ml-0'>
                {category.description}
              </p>
            </div>
          ))}
        </div>

        <div className='space-y-3 pt-2 border-t border-gray-100'>
          <h3 className='text-sm font-semibold text-gray-700'>Email digest</h3>
          <ToggleSwitch
            label='Send me a digest of notifications by email'
            checked={current.emailDigestEnabled}
            onChange={handleDigestToggle}
            yesLabel='On'
            noLabel='Off'
          />
          <p className='text-xs text-gray-500'>
            When on, we&apos;ll group recent unread notifications into one email at
            the frequency you choose. You&apos;ll still see them in your inbox in
            real time.
          </p>

          {current.emailDigestEnabled && (
            <div className='max-w-xs pt-2'>
              <Select
                label='Frequency'
                options={FREQUENCY_OPTIONS}
                selected={frequencySelected}
                setSelected={(opt) => handleFrequencyChange(opt.value)}
                sizeVariant='sm'
              />
            </div>
          )}
        </div>

        {error && (
          <div
            role='alert'
            className='flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3'
          >
            <ExclamationTriangleIcon className='h-5 w-5 shrink-0 text-red-500' />
            <div className='text-sm text-red-700'>
              <p className='font-medium'>We couldn&apos;t save your change</p>
              <p className='mt-0.5'>
                Your notification preferences have been reverted. Please check
                your connection and try again.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
